import { useState } from "react";
import { ArrowRight, ChefHat, Truck, ShoppingBag, Utensils, X } from "lucide-react";

export default function KokkPage() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", about: "" });

  function handleSend() {
    const subject = encodeURIComponent("Søknad som hjemmekokk – Gjesdalivery");
    const body = encodeURIComponent(
      [
        `Navn: ${form.name}`,
        `E-post: ${form.email}`,
        `Telefon: ${form.phone}`,
        "",
        `Om deg og restauranten:\n${form.about}`,
      ].join("\n")
    );
    window.location.href = `mailto:kontakt@gjesdalivery.no?subject=${subject}&body=${body}`;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-[#0f1724] dark:text-white transition-colors">
      {/* HERO */}
      <section className="container mx-auto max-w-5xl px-6 py-24 text-center">
        <PillBadge icon={<ChefHat className="h-4 w-4" />}>Bli hjemmekokk i Gjesdalivery</PillBadge>

        <h1 className="mt-5 text-4xl font-semibold md:text-5xl tracking-tight">
          Lag mat hjemmefra. Vi ordner resten.
        </h1>

        <p className="mt-4 text-lg text-neutral-700 dark:text-white/75 max-w-2xl mx-auto">
          Selg maten du elsker å lage. Du styrer meny og vi fikser emballasje, levering og kunder.
        </p>

        <div className="mt-8 flex justify-center gap-3">
          <PillButton onClick={() => setOpen(true)} primary>
            Søk som hjemmekokk <ArrowRight className="h-4 w-4" />
          </PillButton>

          <PillButton as="a" href="mailto:kontakt@gjesdalivery.no">
            Kontakt oss
          </PillButton>
        </div>
      </section>

      {/* INFO SECTION */}
      <section className="max-w-6xl mx-auto px-6 pb-24 text-center">
        <h2 className="text-2xl font-semibold mb-10">Hvordan det fungerer</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-neutral-700 dark:text-neutral-300">
          <Info icon={<Utensils className="h-10 w-10 text-[#004D99] dark:text-orange-400 mb-3" /> }
                title="Lag maten du elsker">
            Du lager maten hjemme på ditt eget kjøkken, med de rettene du liker best.
          </Info>

          <Info icon={<ShoppingBag className="h-10 w-10 text-[#004D99] dark:text-orange-400 mb-3" /> }
                title="Vi fikser emballasje">
            Du får alt av emballasje og merking klart, slik at alt ser profesjonelt ut.
          </Info>

          <Info icon={<Truck className="h-10 w-10 text-[#004D99] dark:text-orange-400 mb-3" /> }
                title="Vi leverer til kundene">
            Sjåførene våre henter maten og leverer den rett hjem til kundene.
          </Info>
        </div>
      </section>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-white/90 dark:bg-[#121b2e]/95 p-6 shadow-xl backdrop-blur-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xl font-semibold">Søk som hjemmekokk</h4>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 hover:bg-neutral-100 dark:hover:bg-white/10"
                aria-label="Lukk"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Navn"
                placeholder="Fornavn og etternavn"
                value={form.name}
                onChange={(e: any) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                label="E-post"
                type="email"
                placeholder="ola@gjesdalivery.no"
                value={form.email}
                onChange={(e: any) => setForm({ ...form, email: e.target.value })}
              />
              <Input
                label="Telefon"
                type="tel"
                placeholder="+47 456 45 645"
                value={form.phone}
                onChange={(e: any) => setForm({ ...form, phone: e.target.value })}
              />

              <h5 className="pt-2 text-lg font-semibold">Om deg og restauranten</h5>
              <Textarea
                placeholder="Kort om hva du lager, erfaring, alder osv."
                value={form.about}
                onChange={(e: any) => setForm({ ...form, about: e.target.value })}
              />

              <div className="flex justify-end gap-3 pt-2">
                <PillButton onClick={() => setOpen(false)}>Lukk</PillButton>
                <PillButton onClick={handleSend} primary>Send søknad</PillButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------- små byggeklosser ------- */

function PillBadge({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-gray-300/70 bg-white/80 px-3 py-1 text-xs text-gray-700 backdrop-blur-sm
                      dark:border-white/10 dark:bg-white/5 dark:text-white/80">
      {icon} {children}
    </span>
  );
}

function PillButton({
  children,
  primary = false,
  as,
  ...props
}: React.ComponentProps<"button"> &
  React.ComponentProps<"a"> & { primary?: boolean; as?: "a" | "button" }) {
  const base =
    "inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition shadow-sm hover:shadow-md";
  const styles = primary
    ? "bg-[#004D99] hover:bg-[#003366] text-white"
    : "border border-gray-300 bg-white/80 text-gray-900 hover:bg-white dark:border-white/10 dark:bg-white/10 dark:text-white";
  const Comp: any = as || "button";
  return <Comp className={`${base} ${styles}`} {...props}>{children}</Comp>;
}

function Info({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center">
      {icon}
      <h3 className="text-lg font-medium">{title}</h3>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 max-w-xs">{children}</p>
    </div>
  );
}

function Input({ label, className = "", ...props }: any) {
  return (
    <label className="block text-left">
      {label && <span className="mb-1 block text-sm text-neutral-700 dark:text-neutral-300">{label}</span>}
      <input
        {...props}
        className={
          "w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-neutral-900 placeholder-neutral-400 " +
          "focus:outline-none focus:ring-2 focus:ring-[#004D99]/30 focus:border-[#004D99] " +
          "border-neutral-300 dark:border-white/10 dark:bg-[#0f1724] dark:text-white " +
          className
        }
      />
    </label>
  );
}

function Textarea({ label, className = "", ...props }: any) {
  return (
    <label className="block text-left">
      {label && <span className="mb-1 block text-sm text-neutral-700 dark:text-neutral-300">{label}</span>}
      <textarea
        rows={5}
        {...props}
        className={
          "w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-neutral-900 placeholder-neutral-400 " +
          "focus:outline-none focus:ring-2 focus:ring-[#004D99]/30 focus:border-[#004D99] " +
          "border-neutral-300 dark:border-white/10 dark:bg-[#0f1724] dark:text-white " +
          className
        }
      />
    </label>
  );
}
